import React from 'react';
import { createPortal } from 'react-dom';
import Alert from '@amzn/awsui-components-core/alert';
import ExpandableSection from '@amzn/awsui-components-core/expandable-section';
import Spinner from '@amzn/awsui-components-core/spinner';
import type { CatalogScreen, ReportSummary, CatalogTemplate } from 'src/components/star-grid/api/reportEngineTypes';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useStudioTemplatePicker } from 'src/components/star-grid/hooks/useStudioTemplatePicker';
import styles from 'src/components/star-grid/components/styles/StudioTemplatePicker.module.scss';

interface Props {
  readonly text: StarGridText;
  readonly disabled?: boolean;
  /** What the trigger shows. Empty falls back to the placeholder. */
  readonly selectedLabel?: string;
  readonly onSelectTemplate: (template: CatalogTemplate) => void;
  readonly onImportScreen: (screen: CatalogScreen) => void;
  readonly onImportReport: (report: ReportSummary) => void;
}

/**
 * The studio's one Template control: the six templates, then the reports you can import a setup from.
 *
 * Templates and imports live in the SAME popover because they answer the same question — what does
 * this report start as. Splitting them into a dropdown plus a separate panel made the panel read as
 * another axis of configuration rather than an alternative to the dropdown above it.
 *
 * Built as a popover rather than a Cloudscape `Select` because a Select cannot hold collapsible
 * groups, and the 51 deck screens have to arrive collapsed — expanded they bury the six templates.
 */
export const StudioTemplatePicker: React.FC<Props> = ({
  text,
  disabled = false,
  selectedLabel,
  onSelectTemplate,
  onImportScreen,
  onImportReport
}) => {
  const { sources, open, toggle, query, setQuery, visibleProphecy, triggerRef, popoverRef, position, choose } = useStudioTemplatePicker();
  const { templates, saved, loading, error } = sources;

  return (
    <div className={styles.wrapper}>
      <span className={styles.label} id="studio-template-label">
        {text('studioTemplateLabel')}
      </span>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-labelledby="studio-template-label"
        disabled={disabled || loading}
        data-testid="studio-template-trigger"
        ref={triggerRef}
        onClick={toggle}
      >
        <span className={styles.triggerLabel}>{loading ? <Spinner /> : selectedLabel || text('studioTemplatePlaceholder')}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open &&
        position &&
        createPortal(
          <div
            className={styles.popover}
            role="dialog"
            aria-label={text('studioTemplateLabel')}
            data-testid="studio-template-popover"
            ref={popoverRef}
            style={{ top: position.top, left: position.left, width: position.width }}
          >
            {error && <Alert type="error">{error}</Alert>}

            <p className={styles.sectionHeading}>{text('studioTemplatesSection')}</p>
            {templates.length === 0 && !error ? (
              <Alert>{text('studioNoTemplates')}</Alert>
            ) : (
              templates.map((template) => (
                <button
                  type="button"
                  key={template.id}
                  className={styles.option}
                  data-testid={`studio-template-${template.id}`}
                  onClick={choose(() => onSelectTemplate(template))}
                >
                  {template.label}
                </button>
              ))
            )}

            <p className={`${styles.sectionHeading} ${styles.importHeading}`}>{text('studioImportFromExisting')}</p>

            <ExpandableSection variant="footer" headerText={text('studioProphecyReports')}>
              <input
                className={styles.search}
                type="search"
                aria-label={text('studioSearchProphecy')}
                placeholder={text('studioSearchProphecy')}
                value={query}
                data-testid="studio-prophecy-search"
                onChange={(event) => setQuery(event.target.value)}
              />
              {visibleProphecy.length === 0 ? (
                <p className={styles.empty}>{text('studioNoMatches')}</p>
              ) : (
                visibleProphecy.map((group) => (
                  <div key={group.group}>
                    <p className={styles.groupHeading}>{group.group}</p>
                    {group.screens.map((screen) => (
                      <button
                        type="button"
                        key={screen.id}
                        className={styles.screenOption}
                        data-testid={`studio-prophecy-${screen.id}`}
                        onClick={choose(() => onImportScreen(screen))}
                      >
                        <span>{screen.name}</span>
                        {/* A variance screen imports with the compare columns on, and its rows axis is
                          borrowed — worth flagging before the click, not after. */}
                        {screen.variance && <span className={styles.varianceTag}>{text('studioVarianceTag')}</span>}
                      </button>
                    ))}
                  </div>
                ))
              )}
            </ExpandableSection>

            <ExpandableSection variant="footer" headerText={text('studioCustomizedReports')}>
              {saved.length === 0 ? (
                <p className={styles.empty}>{text('studioNoSavedReports')}</p>
              ) : (
                saved.map((report) => (
                  <button
                    type="button"
                    key={report.id}
                    className={styles.screenOption}
                    data-testid={`studio-saved-${report.id}`}
                    disabled={Boolean(report.unavailable_reason)}
                    title={report.unavailable_reason}
                    onClick={choose(() => onImportReport(report))}
                  >
                    <span>{report.name}</span>
                  </button>
                ))
              )}
            </ExpandableSection>
          </div>,
          document.body
        )}
    </div>
  );
};
